
import { Component, Model, Prop, Vue, Watch, Emit } from "vue-property-decorator";
import vSelect3 from "vselect3";
import { filter, debounce, cloneDeep, isEqual } from "lodash";
import api from "@/services/api.service";
import { Getter, namespace } from "vuex-class";
import { UserPermissions, PaginatedSearchQueryParams } from "@/store/types";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import notificationTemplatesApi from "@/services/api.notificationTemplate.service";
import NotificationDocumentTypeId from "@/types/sv-data/enums/NotificationDocumentTypeIds";
import NotificationTemplateTypeId from "@/types/sv-data/enums/NotificationTemplateTypeIds";
import SetEventOutcomeAndNote from "@/components/site-monitor/SetEventOutcomeAndNote.vue";
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import { NotificationTemplateManualField } from "@/types/sv-data/notification-templates/NotificationTemplateManualField";
import axios, { CancelTokenSource } from "axios";
import ContactGroup from "@/types/sv-data/contact-groups/ContactGroup";
import { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import { getDebouncePeriod } from '@/utils/debounce-helper';
import CustomDefinedField from "@/components/custom-fields/CustomDefinedField.vue";
import { CustomFieldDto, CustomFieldTableTypes, CustomFieldValues } from "@/store/custom-fields/types";
import NotificationTemplateFormatOption, { notificationTemplateFormats } from "@/types/sv-data/notification-templates/NotificationTemplateFormatOption";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";
import ContactEmailSelect from "@/components/contacts/ContactEmailSelect.vue";
import { NotifyNotificationRules, NotifyRuleReload, NotifySent, NotifySkipped } from "@/store/site-monitor/types";
import organizationApi from "@/services/api.organizationSettings";
import FlexibleLayout from "../flexible-layout/FlexibleLayout.vue";
import { EventNotification } from "@/types/sv-data/notification-rule/EventNotification";
import { NotificationMixins, ManualFieldInput, ContactOption, NotificationNoteMaxLength, NotificationState, NotificationValidation, BlankNotification, SkippedRequiredNotificationTemplate } from "@/mixins/NotificationMixin";
import { EmailNotificationRequestWithData } from "@/types/sv-data/events/EmailNotificationRequest";
import { BOverlay } from "bootstrap-vue";
import NotificationSummary from "@/components/site-monitor/NotificationSummary.vue";
import UpdateEventOutcomeRequest from "@/types/sv-data/requests/UpdateEventOutcome";
import { Layout } from "@/types/Layout";
import { EventOutcome } from '@/types/EventOutcome';
import { sortOrderByNullLast } from "@/utils/CustomFieldLogic";
import eventsApi from '@/services/api.event.service';
import VirtualAssistSummary from '../ai-virtual-operator/VirtualAssistSummary.vue';

const CustomFields = namespace("customFields");
const SiteMonitor = namespace("siteMonitor");
const FULL_SIZE_COLUMN: number = 12;

interface SentOverlayDetails {
	sentBy: string;
	sentAt: string;
	notificationTitle: string;
	contactsNames: string;
	contactGroupsTitles: string;
	hasAdditionalContacts: boolean;
}

@Component({
	components: {
		"v-select-3": vSelect3,
		"set-event-outcome-and-note": SetEventOutcomeAndNote,
		"custom-field": CustomDefinedField,
		"contact-email-select": ContactEmailSelect,
		"flexible-layout": FlexibleLayout,
		"b-overlay": BOverlay,
		"notification-summary": NotificationSummary,
		"virtual-assist-summary": VirtualAssistSummary
	}
})
export default class NotificationModalContent extends Vue {
	@Prop({ type: Number }) readonly groupId: number;
	@Prop({ type: Number }) readonly eventId: number;
	@Model("change", { type: Boolean }) readonly isVisible: boolean;

	@Prop({type: Boolean, required: false, default: true})
	private notifyOpenedManually: boolean;

	@Prop({ type: Object, required: false})
	private notifyNotificationRules: NotifyNotificationRules;

	@Prop({ type: Boolean, required: false, default: false })
	private isPoppedOut: boolean;

	@Prop({ type: Boolean, required: false, default: false })
	private isQuickEndingEvent: boolean;

	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter getNotificationTemplateDefaultFormat: number;

	@CustomFields.State private notificationTemplateCustomFields: CustomFieldDto[];
	@CustomFields.Action retrieveCustomFields: ({ tableType, live, skipCancel }) => Promise<void>;

	@SiteMonitor.Getter("notificationModalLayout") private notificationModalLayout: Layout;
	@SiteMonitor.Getter("notificationModalLayoutColumnCount") private notificationModalLayoutColumnCount: number;

	@SiteMonitor.Mutation("setNotificationRulesContributingToNotification") private setNotificationRulesContributingToNotification: (notificationRules: NotificationRule[]) => void;

	private layout: Layout = {
		left: [],
		center: ["contacts", "contact-groups", "notification-note", "attach-sit-rep", "notification-templates", "event-outcome", "event-note", "custom-fields"],
		right: []
	}
	private centerColSpan: number = 0;
	private rightColSpan: number = 8;
	private leftColSpan: number = 4;

	private emails: ContactOption[] = [];
	private allContacts: ContactOption[] = [];
	private emailsLoaded: boolean = false;

	private addresses: ContactOption[] = [];
	private selectedContactGroups: ContactGroup[] = [];
	private note: string = "";
	private predefinedNotificationTemplates: NotificationTemplate[] = [];
	private allNotificationTemplates: NotificationTemplate[] = [];
	private selectedTemplateFormat: NotificationTemplateFormatOption = null;
	private orderedCustomFieldList: CustomFieldDto[] = [];
	private eventOutcome: EventOutcomeAndNote = {
		valid: true,
		eventOutcomeNote: null,
		eventOutcomeId: null,
		eventOutcomePath: []
	};
	private defaultNotificationTemplateId: number = null;

	private contactGroupSearchToken: CancelTokenSource = null;
	private contactGroups: ContactGroup[] = [];
	private notificationTemplateFormats: NotificationTemplateFormatOption[] = notificationTemplateFormats;
	private useSingleNotificationUI: boolean = false;

	private sentTemplates: EventNotification[] = [];
	private notificationsToSend: NotificationState[] = [];
	private notificationValidationErrorsByTemplate: Map<number, NotificationValidation> = new Map<number, NotificationValidation>();
	private manualNotificationIsValid: boolean = false;
	private showActionPlanAlert: boolean = false;
	private footerStep: number = 1;
	private landingPageFooterStep: number = Number.MAX_SAFE_INTEGER;

	private requiredContactMessage: string = "This contact was not included when originally sent, but is required now as a part of a notification rule.";
	private recipientInputsTouched: boolean = false;
	private highlightedContactGroups: ContactGroup[] = [];
	private isComponentReady: boolean = false;
	private currentNotificationPreviouslySent: boolean = false;
	private currentNotificationSkipped: boolean = false;

	private savedNotificationsByTemplateId: Map<number, NotificationState> = new Map();
	private sentTemplatesByTemplateId: Map<number, NotificationState> = new Map();
	private savedNotification: NotificationState = null;
	private isNotificationChangedFromSaved: boolean = false;

	private nonTemplateRuleId: number = -1;
	private triggeredNotificationRules: Map<number, NotificationRule[]> = new Map();

	private showTooltip: boolean = false;

	private get selectedNotificationTemplate(): NotificationTemplate {
		return this.currentNotificationToSend?.notificationTemplate;
	}

	private get hasOneTemplate(): boolean {
		return !this.isPredefineTemplateEnabled || this.notifyOpenedManually || this.notificationRulesHasNoTemplate;
	}

	private set selectedNotificationTemplate(notificationTemplate: NotificationTemplate) {
		if (!this.currentNotificationToSend) {
			return;
		}

		this.currentNotificationToSend.notificationTemplate = notificationTemplate;
	}

	private get footerStepTotal(): number {
		return this.notificationsToSend.length || 1;
	}

	private get totalPageCount(): number {
		// Add 1 for the landing page step + the footerStepTotal for the number of notifications
		// Seperate getter for this as footerStepTotal is used to calculate if the user is on a template or not.
		return this.footerStepTotal + 1;
	}

	private onPrevFooterStep(): void {
		this.showTooltip = false;
		if (this.footerStep != this.landingPageFooterStep && this.footerStep > 1) {
			this.validateNotification(this.currentNotificationToSend);
			this.footerStep--;
			this.setSelectedNotificationTemplateByStep();
		}
		else {
			this.footerStep = this.maxFooterStep
		}
	}

	private get isSitRepEnabledByDefault(): boolean {
		return !this.isNotificationTemplatesEnabled;
	}

	private onNextFooterStep(): void {
		// Validate current template before moving onto next
		this.validateNotification(this.currentNotificationToSend);

		this.showTooltip = false;

		if (this.isOnFinalTemplateStep) {
			this.footerStep = this.landingPageFooterStep
			this.currentNotificationPreviouslySent = false;
			this.currentNotificationSkipped = false;

			this.setNotificationRulesContributingToNotification([]);
		}

		if (this.footerStep < this.footerStepTotal) {
			this.footerStep++;
			this.setSelectedNotificationTemplateByStep();
		}
	}

	private setSelectedNotificationTemplateByStep(): void {
		const notification = this.notificationsToSend[this.footerStep - 1];
		if (notification && notification.notificationTemplate) {
			this.selectedNotificationTemplate = notification.notificationTemplate;
		}

		this.updateAvailableManualFields(this.currentNotificationToSend?.notificationTemplate, null)
	}

	private async mounted(): Promise<void> {
		this.updateModalLayout();

		if (this.isNotificationTemplatesCustomFieldsEnabled) {
			await this.retrieveCustomFields({ tableType: CustomFieldTableTypes.NotificationTemplate, live: true, skipCancel: false });
			this.orderedCustomFieldList = this.notificationTemplateCustomFields;
			sortOrderByNullLast(this.orderedCustomFieldList);
		}

		await this.loadContacts();

		if (this.isContactGroupsEnabled) {
			await this.retrieveContactGroups();
		}

		if (!this.notifyOpenedManually) {
			await this.collateNotificationsToSend();
			this.selectedNotificationTemplate = this.notificationsToSend[0]?.notificationTemplate;
		} else {
			await this.loadTemplatesFromApi();
			// Create blank notification
			this.notificationsToSend = [
				NotificationMixins.methods.createBlankNotification(this.isSitRepEnabledByDefault)
			];
		}

		// If opened manually or predefine template feature is disabled, load the default template
		if (this.notifyOpenedManually || !this.isPredefineTemplateEnabled) {
			await this.loadDefaultTemplate();
		}

		this.isComponentReady = true;
	}

	private buildTriggeredNotificationRules(notificationRules: NotificationRule[]): void {
		notificationRules.forEach((rule: NotificationRule) => {
			const templateId = rule.notificationTemplateId && this.isPredefineTemplateEnabled ? rule.notificationTemplateId : this.nonTemplateRuleId;
			if (this.triggeredNotificationRules.has(templateId)) {
				const existingRules = this.triggeredNotificationRules.get(templateId);
				existingRules.push(rule);
				this.triggeredNotificationRules.set(templateId, existingRules);
			} else {
				this.triggeredNotificationRules.set(templateId, [rule]);
			}
		});
	}

	@Watch("notifyNotificationRules", { immediate: false, deep: true })
	private async onNotifyNotificationRulesChanged(newVal: NotifyNotificationRules | null, oldVal: NotifyNotificationRules | null): Promise<void>{
		if (!this.canUseNotificationRules) {
			return;
		}

		// if all the rule IDs in new val are the same as old val, return
		if (newVal && oldVal) {
			const newRuleIds = newVal?.rules.map(r => r.notificationRuleId).sort();
			const oldRuleIds = oldVal?.rules.map(r => r.notificationRuleId).sort();

			if (isEqual(newRuleIds, oldRuleIds)) {
				return;
			}
		}

		await this.collateNotificationsToSend();
	}

	private async collateNotificationsToSend(): Promise<void>{
		// Fetch Contacts and Contact Groups from rules which have no notification template assigned, and rules which contain the same template multiple times
		if (!this.notifyNotificationRules || !this.notifyNotificationRules.rules) {
			return;
		}

		this.buildTriggeredNotificationRules(this.notifyNotificationRules.rules);

		this.useSingleNotificationUI = false;

		const groupingMethod = this.isPredefineTemplateEnabled ?
			NotificationMixins.methods.groupContactsAndGroupsByTemplate :
			NotificationMixins.methods.groupContactsAndGroups;

		groupingMethod.bind(NotificationMixins.methods);

		const {
			contactsByTemplate,
			contactGroupsByTemplate,
			contactsWithNoTemplate,
			contactGroupsWithNoTemplate,
			emailAddressesWithNoTemplate
		} = groupingMethod(this.notifyNotificationRules?.rules);

		if (this.isPredefineTemplateEnabled && !this.notifyOpenedManually
			&& (contactsByTemplate.size > 0 || contactGroupsByTemplate.size > 0))
		{
			await this.loadTemplatesFromRules();
			this.buildMultipleNotificationsForEndEvent(
				contactsByTemplate,
				contactGroupsByTemplate,
				contactsWithNoTemplate,
				contactGroupsWithNoTemplate,
				emailAddressesWithNoTemplate
			);
		} else {
			await this.loadTemplatesFromApi();
			this.buildSingleNotificationForEndEvent(
				contactsWithNoTemplate,
				contactGroupsWithNoTemplate,
				emailAddressesWithNoTemplate
			);
		}
    }

	private createAndPopulateNotification(
		userIds: number[],
		contactGroupIds: string[],
		emailAddresses: string[],
		templateId: number | null,
		templateData: NotificationState | null = null,
		markAsPreviouslySent: boolean = false
	): NotificationState {
		// Create the Notification object with the default values
		const notification = NotificationMixins.methods.createNotification(
			userIds,
			contactGroupIds,
			emailAddresses,
			this.allContacts,
			this.contactGroups,
			this.allNotificationTemplates,
			templateId,
			this.isSitRepEnabledByDefault
		);

		// Load template data (saved, sent, or blank)
		NotificationMixins.methods.loadDataFromTemplate(
			notification,
			templateData,
			this.allContacts,
			this.contactGroups
		);

		// Set default template format if not already set and template exists
		if (notification.notificationTemplate && !notification.notificationTemplateFormat && this.isNotificationTemplatesEnabled) {
			const defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.getNotificationTemplateDefaultFormat);
			if (defaultTemplateFormatIndex !== -1) {
				notification.notificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
			}
		}

		if (markAsPreviouslySent) {
			notification.isPreviouslySent = true;
		}

		return notification;
	}

	private buildSingleNotificationForEndEvent(
		contactsWithNoTemplate: number[],
		contactGroupsWithNoTemplate: string[],
		emailAddressesWithNoTemplate: string[]
	): void {
		this.notificationsToSend = [];
		const notification = this.createAndPopulateNotification(
			contactsWithNoTemplate,
			contactGroupsWithNoTemplate,
			emailAddressesWithNoTemplate,
			null
		);

		const defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.getNotificationTemplateDefaultFormat);

		if (this.isNotificationTemplatesEmbeddedHtmlEnabled || this.getNotificationTemplateDefaultFormat !== NotificationDocumentTypeId.Html) {
			notification.notificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
		}

		// Create a Notify modal screen for this template
		this.notificationsToSend.push(notification);
		this.useSingleNotificationUI = true;
	}

	private buildMultipleNotificationsForEndEvent(
		contactsByTemplate: Map<number, number[]>,
		contactGroupsByTemplate: Map<number, string[]>,
		contactsWithNoTemplate: number[],
		contactGroupsWithNoTemplate: string[],
		emailAddressesWithNoTemplate: string[]
	): void {
		this.notifyNotificationRules.rules.forEach(async (rule: NotificationRule) => {
			// If no template assigned, we do not want to create a Notify screen for it
            if (!rule.notificationTemplateId) {
				return;
			}

			// If template not already in allNotificationTemplates, fetch and add it
			if (!this.allNotificationTemplates.some((t: NotificationTemplate) =>
				t.notificationTemplateId === rule.notificationTemplateId)) {
				if (rule.notificationTemplate == null) {
					return;
				}

				this.allNotificationTemplates.push(rule.notificationTemplate);
			}

			// Avoid creating multiple Notify screens for a template which belongs to multiple rules
            if (this.notificationsToSend.some((n: NotificationState) =>
				n.notificationTemplate?.notificationTemplateId === rule.notificationTemplateId)) {
				return;
			}

			// Gather all Contacts and Contact Groups for this template
            const userIdsForRule = NotificationMixins.methods.combineAndRemoveDuplicates(
                contactsByTemplate.get(rule.notificationTemplateId) || [],
                contactsWithNoTemplate
			);

            const contactGroupIdsForRule = NotificationMixins.methods.combineAndRemoveDuplicates(
                contactGroupsByTemplate.get(rule.notificationTemplateId) || [],
                contactGroupsWithNoTemplate
			);

			const emailAddressesForRule = NotificationMixins.methods.combineAndRemoveDuplicates(
				rule.emailAddresses || [],
				emailAddressesWithNoTemplate
			);

			// Determine template data source: sent > saved > blank
			let templateData: NotificationState | null = null;
			let isPreviouslySent = false;

			if (this.sentTemplatesByTemplateId.has(rule.notificationTemplateId)) {
				templateData = this.sentTemplatesByTemplateId.get(rule.notificationTemplateId);
				isPreviouslySent = true;
			} else if (this.isSaveNotificationEnabled && this.savedNotificationsByTemplateId.has(rule.notificationTemplateId)) {
				templateData = this.savedNotificationsByTemplateId.get(rule.notificationTemplateId);
			}

			const notification = this.createAndPopulateNotification(
				userIdsForRule,
				contactGroupIdsForRule,
				emailAddressesForRule,
				rule.notificationTemplateId,
				templateData,
				isPreviouslySent
			);

			// Create a Notify modal screen for this template
            this.notificationsToSend.push(notification);
        });
	}

	private get notificationRuleContactGroups(): ContactGroup[] {
		const contactGroups: ContactGroup[] = [];

		if (this.notifyOpenedManually) {
			return [];
		}

		if (this.canUseNotificationRules) {
			this.notifyNotificationRules?.rules.forEach(rule => {
				if (rule.notificationTemplateId && rule.notificationTemplateId !== this.currentNotificationToSend?.notificationTemplate?.notificationTemplateId) {
					return;
				}

				rule.contactGroupIds.forEach(contactGroupId => {
					const contactGroup = this.contactGroups.firstOrDefault((c: ContactGroup) => c.contactGroupId == contactGroupId);
					if (contactGroup && !contactGroups.some(cg => cg.contactGroupId === contactGroup.contactGroupId)) {
						contactGroups.push(contactGroup);
					}
				});
			});
		}

		return contactGroups;
	}

	private get notificationRuleContacts(): ContactOption[] {
		const contacts: ContactOption[] = [];

		if (this.notifyOpenedManually) {
			return [];
		}

		if (this.canUseNotificationRules) {
			this.notifyNotificationRules?.rules.forEach(rule => {
				if (rule.notificationTemplateId && rule.notificationTemplateId !== this.currentNotificationToSend?.notificationTemplate?.notificationTemplateId) {
					return;
				}

				rule.userIds.forEach(userId => {
					const contact = this.allContacts.firstOrDefault((c: ContactOption) => c.userID == userId);
					if (contact && !contacts.some(c => c.userID === contact.userID)) {
						contacts.push(contact);
					}
				});

				contacts.push(...rule.emailAddresses.map(email => ({ userID: null, email: email, fullName: null })));
			});
		}

		return contacts;
	}

	private get highlightedContacts(): ContactOption[] {
		const notificationRuleContact = this.notificationRuleContacts;
		if (notificationRuleContact.length === 0) {
			return [];
		}

		if (!this.currentNotificationToSend || !this.currentNotificationToSend.notificationTemplate) {
			return [];
		}

		const prevSent = this.recentEventSentTemplate(this.currentNotificationToSend.notificationTemplate.notificationTemplateId);

		if (!prevSent) {
			return [];
		}

		const notificationData = NotificationMixins.conversionMethods.eventNotificationToNotification(prevSent, this.allNotificationTemplates);
		if (!notificationData || !notificationData.contacts) {
			return [];
		}

		// Find contacts that are in the notificationRuleContact but not in the current notification contacts
		return filter(notificationRuleContact, (contact) => {
			return !notificationData.contacts.some(c =>
				(c.fullName == null && c.email === contact.email)
				|| (c.fullName != null && (c.fullName === contact.fullName && c.email === contact.email))
			);
		});
	}

	@Emit("notify-skipped")
	private skipSingleRule(): NotifySkipped {
		return {
			groupId: this.groupId,
			eventOutcomeId: this.eventOutcomeId,
			eventNote: this.eventNote
		};
	}

	@Emit("notify-skipped-predefined-templates")
	private auditSkippedPredefinedNotificationTemplates(skippedRequiredNotificationTemplates: SkippedRequiredNotificationTemplate[]): SkippedRequiredNotificationTemplate[] {
		return skippedRequiredNotificationTemplates;
	}

	private buildHighlightedContactGroups(): ContactGroup[] {
		const notificationRuleContact = this.notificationRuleContactGroups;
		if (notificationRuleContact.length === 0) {
			return [];
		}

		if (!this.currentNotificationToSend || !this.currentNotificationToSend.notificationTemplate) {
			return [];
		}

		const prevSent = this.recentEventSentTemplate(this.currentNotificationToSend.notificationTemplate.notificationTemplateId);

		if (!prevSent) {
			return [];
		}

		const notificationData = NotificationMixins.conversionMethods.eventNotificationToNotification(prevSent, this.allNotificationTemplates);
		if (!notificationData || !notificationData.contactGroups) {
			return [];
		}

		// Find contacts that are in the notificationRuleContact but not in the current notification contacts
		return filter(notificationRuleContact, (contact) => {
			return !notificationData.contactGroups.some(c =>
				(c.contactGroupId === contact.contactGroupId)
			);
		});
	}

	private get isNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates"]);
	}

	private get isSaveNotificationEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates","SaveNotifications"]);
	}

	private get isContactGroupsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationGroups"]);
	}

	private get isNotificationTemplatesCustomFieldsEnabled(): boolean {
		return this.getFeature(["CustomFields", "NotificationTemplates"]);
	}

	private get isNotificationTemplatesEmbeddedHtmlEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "EmbeddedHtml"]);
	}

	private get isPersistEventOutcomeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "PersistEventOutcome"]);
	}

	private get isNotificationRulesEnabled(): boolean {
		return !!this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationRules"]);
	}

	private get isNotificationModalLayoutEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "RearrangedLayout"]);
	}

	private get isNotificationNoteEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationNote"]);
	}

	private get isPredefineTemplateEnabled(): boolean {
        return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationRules", "PredefineTemplate"]);
	}

	private get canUseVirtualAssist(): boolean {
		return this.getFeature(["AI", "VirtualAssist", "NotificationModal"]) && this.permissions.canUseVirtualAssistSummary;
	}

	private get hasNotificationTemplatePermissions(): boolean {
		return this.permissions.canViewNotificationTemplates || this.permissions.canEditNotificationTemplates;
	}

	private get hasCanViewCustomFieldsPermission(): boolean {
		return this.permissions.canViewCustomFields;
	}

	private get eventNote(): string {
		return this.notifyNotificationRules?.eventNote ?? "";
	}

	private get eventOutcomePath(): EventOutcome[] {
		return this.notifyNotificationRules?.eventOutcomePath ?? [];
	}

	private get eventOutcomeId(): number {
		return this.notifyNotificationRules?.eventOutcomePath[this.notifyNotificationRules.eventOutcomePath.length - 1]?.eventOutcomeID;
	}

	private get hasNotificationRulePermissions(): boolean {
		return this.permissions.canViewNotificationRules || this.permissions.canEditNotificationRules;
	}

	private get canUseNotificationRules(): boolean {
		return this.isNotificationRulesEnabled && this.hasNotificationRulePermissions
	}

	private get notificationTemplateIdsForMatchingRules(): number[] {
		if (!this.canUseNotificationRules || !this.notifyNotificationRules?.rules) {
			return [];
		}
		const notificationTemplateIds = this.notifyNotificationRules.rules.map((rule) => {
			return rule.notificationTemplateId;
		});

		return notificationTemplateIds;
	}

	private get canUseNotificationTemplates(): boolean {
		return this.isNotificationTemplatesEnabled && this.hasNotificationTemplatePermissions;
	}

	@Watch("groupId")
	private async loadNotificationTemplates(): Promise<void> {
		if (!this.canUseNotificationTemplates) {
			return;
		}

		try {
			// Fetch all notification options
			this.allNotificationTemplates = await notificationTemplatesApi.getAllNotificationTemplateRenders(NotificationTemplateTypeId.Event, this.groupId);
		} catch (ex) {
			console.error("Unexpected error fetching notification templates: ", ex);
		}
	}

	private updateModalLayout(): void {
		if (this.isNotificationModalLayoutEnabled) {
			this.layout = this.notificationModalLayout;

			if (this.notificationModalLayoutColumnCount !== 1) {
				return;
			}

			if (this.layout.left.length > 0) {
				this.leftColSpan = FULL_SIZE_COLUMN;
			} else if (this.layout.center.length > 0) {
				this.centerColSpan = FULL_SIZE_COLUMN;
			} else if (this.layout.right.length > 0) {
				this.rightColSpan = FULL_SIZE_COLUMN;
			}
		}
	}

	private get userIdsForNotificationRules(): number[] {
		const userIdsFromRules: number[] = [];

		if (this.canUseNotificationRules) {
			this.notifyNotificationRules?.rules.forEach(rule => {
				userIdsFromRules.push(...rule.userIds);
			})
		}

		return Array.from(new Set(userIdsFromRules))
	}

	private async getAllContacts(): Promise<void> {
		try {
			this.allContacts = await api.getContactsPaginated({
				params: {
					page: 1,
					pageSize: 0,
					searchTerm: "",
					requiredUserIds: this.userIdsForNotificationRules
				},
				cancelToken: null
			}, this.groupId);
		} catch (error) {
			console.error("Error when getting all contacts", error)
		}
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
			await this.retrieveContactGroups(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true });

	@Watch("groupId")
	private async retrieveContactGroups(searchTerm: string = ""): Promise<void> {
		const params: PaginatedSearchQueryParams = {
			page: 1,
			pageSize: 50,
			sortBy: "title",
			searchTerm: searchTerm
		}

		this.contactGroupSearchToken = axios.CancelToken.source();

		const request: PaginatedSearchRequest = {
			params: params,
			cancelToken: this.contactGroupSearchToken.token
		}

		try {
			this.contactGroups = (await api.getContactGroups(request)).data;
		}
		catch (err) {
			console.error(err);
			this.$notify({
				type: "error",
				title: "Retrieving Contact Groups",
				text: err.message
			});
			return;
		}
	}

	@Watch("groupId")
	private async loadContacts(): Promise<void> {
		this.emailLoading(false);
		await this.getAllContacts();
		this.emailLoading(true);
	}

	private get notifyRules(): NotificationRule[] {
		return this.notifyNotificationRules?.rules ?? null;
	}

	private onSave(): void {
		this.handleSave();
	}

	private handleSave(): void {
		debounce(async (): Promise<void> => {
			if (!this.isSaveNotificationEnabled){
				return;
			}

			try {
				const payloadWithData = this.getEmailPayload();

				if (payloadWithData == null || payloadWithData.length === 0 || payloadWithData.length > 1) {
					this.$notify({
						type: "error",
						title: "Saving Notification",
						text: "Can only save a single notification progress at a time."
					});
					return;
				}

				const notificationToSave = payloadWithData[0];
				const saveResult = await api.saveEventNotification(this.eventId, notificationToSave);

				// Save successful
				if (saveResult){
					this.savedNotification = cloneDeep(this.currentNotificationToSend);
					this.isNotificationChangedFromSaved = false;
					this.$notify({
						type: "success",
						title: "Saving Notification",
						text: "Notification progress saved successfully."
					});
				}
			}
			catch(err) {
				console.error('Unexpected error when saving notification:', err);
				this.savedNotification = null;
				this.isNotificationChangedFromSaved = true;
				this.$notify({
					type: "error",
					title: "Saving Notification",
					text: "Failed to save notification progress. Please try again."
				});
			}
			finally {
				// Re-fetch saved templates to ensure we have the latest
				await this.retrieveSavedTemplates();
			}
		}, getDebouncePeriod())();
	}

	private onSend(): void {
		this.handleSend();
	}

	private handleSend(skipAll: boolean = false): void {
		debounce(async (): Promise<void> => {
			const groupId = this.groupId;
			const eventId = this.eventId;
			const eventOutcome = this.eventOutcome;

			const payloadsWithData = skipAll ? [] : this.getEmailPayload();

			const skippedNotificationTemplates: SkippedRequiredNotificationTemplate[] = this.notificationsToSend
				.filter(notification => skipAll || notification.skipped && notification.notificationTemplate?.notificationTemplateId != null)
				.map(notification => ({
					notificationTemplateId: notification.notificationTemplate.notificationTemplateId,
					notificationTemplateName: notification.notificationTemplate.title
				}));

			if (eventOutcome.eventOutcomeId != null || (eventOutcome.eventOutcomeNote != null && eventOutcome.eventOutcomeNote.length > 0)) {
				try {
					let payload: UpdateEventOutcomeRequest = {
						eventId: this.eventId,
						eventOutcomeId: eventOutcome.eventOutcomeId,
						note: eventOutcome.eventOutcomeNote
					};
					await api.updateEventOutcomeByNotification(payload);
				}
				catch(err) {
					console.error(err);
					this.$notify({
						type: "error",
						title: "Updating Event Outcome",
						text: err.message
					});

					// Break out of function so notification doesn't get sent
					return;
				}
			}

			try {
				this.notifySent({
					groupId: groupId,
					eventOutcomeId: eventOutcome.eventOutcomeId,
					eventNote: eventOutcome.eventOutcomeNote
				});
				if (skippedNotificationTemplates.length > 0) {
					this.auditSkippedPredefinedNotificationTemplates(skippedNotificationTemplates);
				}
				if (!payloadsWithData || payloadsWithData?.length === 0) {
					return;
				}
				await api.sendEventNotifications(eventId, payloadsWithData);
			}
			catch (err) {
					console.error(err);
					this.$notify({
						type: "error",
						title: "Sending Event Notifications",
						text: err.message
					});

					// Break out of function so notification doesn't get sent
					return;
			}
			finally {
				if (this.isSaveNotificationEnabled && this.savedNotificationsByTemplateId.size > 0) {
					// Re-fetch saved templates to ensure we have the latest
					await this.retrieveSavedTemplates();
				}
			}
		}, getDebouncePeriod())();
	}

	private getEmailPayload(): EmailNotificationRequestWithData[] {
		// If ad hoc Notify modal, only send current notification
		if (this.notifyOpenedManually || !this.isPredefineTemplateEnabled || this.useSingleNotificationUI) {
			const payload = NotificationMixins.conversionMethods.notificationToEmailRequest(this.currentNotificationToSend);
			return [{
				notificationRequest: payload,
				notificationData: JSON.stringify(payload)
			}];
		} else {
			const skippedTemplateIds: number[] = []
			const payloadsWithData: EmailNotificationRequestWithData[] = [];
			this.notificationsToSend.forEach(notification => {
				// Do not process 'Previously Sent' notification templates
				if (notification.isPreviouslySent) {
					return;
				}

				// Collect Notification Template ID values of skipped (and not previously-sent) templates for auditing
				if (notification.skipped) {
					const notificationTemplateId = notification.notificationTemplate?.notificationTemplateId;
					if (notificationTemplateId != null){
						skippedTemplateIds.push(notificationTemplateId);
					}
					return;
				}

				// Process completed notification templates
				const payload = NotificationMixins.conversionMethods.notificationToEmailRequest(notification);
				payloadsWithData.push({
					notificationRequest: payload,
					notificationData: JSON.stringify(payload)
				});
			});

			return payloadsWithData;
		}
	}

	private async onPreviewTemplate(): Promise<void> {
		let override = {};
		if (this.eventOutcome?.eventOutcomeNote != null && this.eventOutcome?.eventOutcomeNote.length > 0) {
			override["eventOutcomeNote"] = this.eventOutcome.eventOutcomeNote;
		}

		// Hard coded to PDF as XSS middleware doesn't like HTML file responses
		// and this will need to be investigated
		await notificationTemplatesApi.previewNotificationTemplate(this.eventId, {
			notificationTemplateId: this.selectedNotificationTemplate.notificationTemplateId,
			notificationDocumentTypeId: NotificationDocumentTypeId.Pdf,
			eventOutcomeOverride: this.eventOutcome?.eventOutcomeId,
			placeholderOverrides: override,
			note: this.currentNotificationToSend.note,
			manualInputFields: this.currentNotificationToSend.notificationManualFields != null ? this.currentNotificationToSend.notificationManualFields.reduce((map, obj) => {
				map[obj.field.id] = obj.value;
				return map;
			}, {}) : [],
			customFieldInputs: this.currentNotificationToSend.notificationCustomFields != null ? this.currentNotificationToSend.notificationCustomFields.reduce((map, obj) => {
				map[obj.field.id] = obj.value;
				return map;
			}, {}) : []
		});
	}

	private updateAvailableManualFields(newVal: NotificationTemplate, oldVal: NotificationTemplate): void {
		if (this.selectedNotificationTemplate == null) {
			this.selectedTemplateFormat = null;
			this.currentNotificationToSend.notificationManualFields = [];
			this.currentNotificationToSend.notificationCustomFields = [];
			return;
		}

		if (newVal === oldVal) {
			return;
		}

		this.isComponentReady = false;

		if (this.isContactGroupsEnabled && !!this.selectedNotificationTemplate.contactGroups) {
			this.selectedNotificationTemplate.contactGroups.forEach(contactGroup => {
				if (!this.selectedContactGroups.some(id => id.contactGroupId === contactGroup.contactGroupId)) {
					this.selectedContactGroups.push(contactGroup);
				}
			});

			if (!this.notifyOpenedManually) {
				this.highlightedContactGroups = this.buildHighlightedContactGroups();
			}
		}
		// Load the manual/custom fields in ad hoc Notify modal
		if (this.notifyOpenedManually || (!this.notifyOpenedManually && (!this.isPredefineTemplateEnabled || this.useSingleNotificationUI))) {
			this.currentNotificationToSend.notificationTemplate = this.selectedNotificationTemplate;

			// Determine template data source: sent > saved > blank
			let templateData: NotificationState | null = null;
			let isPreviouslySent = false;

			if (this.sentTemplatesByTemplateId.has(this.selectedNotificationTemplate?.notificationTemplateId)) {
				templateData = this.sentTemplatesByTemplateId.get(this.selectedNotificationTemplate?.notificationTemplateId);
				isPreviouslySent = true;
			} else if (this.isSaveNotificationEnabled && this.savedNotificationsByTemplateId.has(this.selectedNotificationTemplate?.notificationTemplateId)) {
				templateData = this.savedNotificationsByTemplateId.get(this.selectedNotificationTemplate?.notificationTemplateId);
			}

			NotificationMixins.methods.loadDataFromTemplate(
				this.currentNotificationToSend,
				templateData,
				this.allContacts,
				this.contactGroups
			);

			if (isPreviouslySent) {
				this.currentNotificationToSend.isPreviouslySent = true;
			}
		}
		this.isComponentReady = true;
	}

	private async retrieveSavedTemplates(): Promise<void> {
		if (!this.canUseNotificationTemplates || !this.isSaveNotificationEnabled) {
			return;
		}

		const savedTemplates = await eventsApi.getSavedEventNotifications(this.eventId);
		if (savedTemplates && Array.isArray(savedTemplates)) {
			this.savedNotificationsByTemplateId.clear();

			savedTemplates.forEach(template => {
				try {
					const savedNotification = NotificationMixins.conversionMethods.eventNotificationToNotification(
						template,
						this.allNotificationTemplates
					);
					if (savedNotification && savedNotification.notificationTemplate) {
						this.savedNotificationsByTemplateId.set(
							savedNotification.notificationTemplate.notificationTemplateId,
							savedNotification
						);
					}
				} catch (ex) {
					console.error("Error converting saved template to notification state: ", ex);
				}
			});
		}
	}

	private async retrieveSentTemplates(): Promise<void> {
		if (!this.canUseNotificationTemplates) {
			return;
		}

		this.sentTemplatesByTemplateId.clear();
		let rawTemplates = [];
		try {
			rawTemplates = await eventsApi.getSentEventNotifications(this.eventId);
		}
		catch (ex) {
			console.error("Unexpected error fetching previously-sent notification templates: ", ex);
		}
		// Sort templates into descending order by sent date
		rawTemplates.sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

		this.sentTemplates = rawTemplates;
		rawTemplates.forEach(template => {
			const parsed = NotificationMixins.conversionMethods.eventNotificationToNotification(
				template,
				this.allNotificationTemplates
			);

			// Store in map for easy retrieval when auto-filling notifications
			if (parsed && parsed?.notificationTemplate?.notificationTemplateId && !this.sentTemplatesByTemplateId.has(parsed.notificationTemplate.notificationTemplateId)) {
				this.sentTemplatesByTemplateId.set(
					parsed.notificationTemplate.notificationTemplateId,
					parsed
				);
			}
		});
	}

	private recentEventSentTemplate(templateId: number): EventNotification | null {
		if (!this.sentTemplates || this.sentTemplates.length === 0) {
			return null;
		}

		const sortedSentTemplates = this.sentTemplates
			.sort((a: EventNotification, b: EventNotification) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
			.find(st => st.notificationTemplateId === templateId);

		return sortedSentTemplates ?? null;
	}

	@Watch("currentNotificationToSend.notificationTemplateFormat")
	public onNotificationTemplateFormatChanged(newFormat: NotificationTemplateFormatOption): void {
		this.selectedTemplateFormat = newFormat;
	}

	private get errorsExistInTemplates(): boolean {
		// Return false if all templates are skipped
		if (this.notificationsToSend.every(n => n.skipped)) {
			return false;
		}
		// Only consider non-skipped notifications
		for (const notification of this.notificationsToSend) {
			if (notification.skipped) {
				continue;
			}
			const errors = this.notificationValidationErrorsByTemplate.get(notification.notificationTemplate?.notificationTemplateId);
			if (errors && NotificationMixins.methods.notificationHasErrors(errors)) {
				return true;
			}
		}
		return false;
	}

	private async eventOutcomeDetailsChanged(newValue: EventOutcomeAndNote): Promise<void> {
		if (this.canUseNotificationRules && !this.notifyOpenedManually) {
			this.emitNotifyRuleReload({
				eventId: this.eventId,
				eventOutcomePath: newValue.eventOutcomePath,
				eventOutcomeNote: newValue.eventOutcomeNote
			})
		}
		this.eventOutcome = newValue;

		if (this.isPersistEventOutcomeEnabled && this.eventOutcome?.eventOutcomeId != null) {
			this.emitEventOutcomeChange(this.eventOutcome);
		}
	}

	private get sentTemplateMessage() : string {
		const sentTemplate = this.sentTemplates.find(st => st.notificationTemplateId === this.selectedNotificationTemplate?.notificationTemplateId);
		if (!sentTemplate) {
			return "";
		}

		return `Template was sent at ${new Date(sentTemplate.sentAt).toLocaleString()} by ${sentTemplate.userName}. All sent information is detailed in the fields below `;
	}

	@Emit("event-outcome-changed")
	private emitEventOutcomeChange(newValue: EventOutcomeAndNote): EventOutcomeAndNote {
		return newValue;
	}

	private setDefaultTemplateFormat(): void {
		if (this.isNotificationTemplatesEnabled && (
			this.isNotificationTemplatesEmbeddedHtmlEnabled
			|| this.getNotificationTemplateDefaultFormat !== NotificationDocumentTypeId.Html
		)) {
			const defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.getNotificationTemplateDefaultFormat);
			this.selectedTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
			this.currentNotificationToSend.notificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
		}
	}

	private onSearch(searchTerm: string): void{
		this.handleSearchInput(searchTerm);
	}

	private getCustomFieldSetting(customFieldId: string): CustomFieldDto {
		if (!this.orderedCustomFieldList || this.orderedCustomFieldList.length === 0 || customFieldId == null) {
			return null;
		}
		return this.orderedCustomFieldList.find(cf => cf.id == customFieldId);
	}

	private getCustomFieldValue(customField: NotificationTemplateManualField): CustomFieldValues {
		let value: string = "";
		const existing: ManualFieldInput = this.currentNotificationToSend.notificationCustomFields.firstOrDefault(cf => cf.field?.id == customField?.id);

		if (existing) {
			value = existing.value;
		}

		const customFieldValue = {
			id: customField.id,
			value: value
		}

		return customFieldValue;
	}

	private get currentNotificationToSend(): NotificationState {
		return this.notificationsToSend[this.footerStep - 1];
	}

	private get isSkipped(): boolean {
		return this.currentNotificationToSend?.skipped;
	}

	private onCustomFieldValueChanged(updatedValue: CustomFieldValues): void {
		const customFieldBeingChanged = this.currentNotificationToSend.notificationCustomFields.find(cf => cf.field.id === updatedValue.id);
		if (customFieldBeingChanged) {
			customFieldBeingChanged.value = updatedValue.value;
		}
	}

	private get canDisplayCustomFields(): boolean {
		return this.hasCustomFieldPermissions
		&& this.selectedNotificationTemplate.customFields
		&& this.selectedNotificationTemplate.customFields.length > 0;
	}

	private get hasCustomFieldPermissions(): boolean {
		return this.selectedNotificationTemplate != null
		&& this.isNotificationTemplatesCustomFieldsEnabled
		&& this.hasCanViewCustomFieldsPermission;
	}

	private get notificationTemplateDropdownDisabled(): boolean {
		return this.currentNotificationToSend && !this.notifyOpenedManually && this.isPredefineTemplateEnabled && !this.notificationRulesHasNoTemplate;
	}

	private sortCustomFieldsByOrder(customFields: NotificationTemplateManualField[]): NotificationTemplateManualField[] {
		const orderedCustomFields: NotificationTemplateManualField[] = [];

		this.orderedCustomFieldList.forEach(ocf => {
			const matchedCustomField = customFields.find(cf => cf.id == ocf.id);

			if (matchedCustomField) {
				orderedCustomFields.push(matchedCustomField);
			}
		});

		return orderedCustomFields;
	}

	private get getNotificationTemplateFormats(): NotificationTemplateFormatOption[] {
		if(!this.isNotificationTemplatesEmbeddedHtmlEnabled ) {
			// Filter out the HTML format if the feature is not enabled
			return this.notificationTemplateFormats.filter((format: NotificationTemplateFormatOption) => {
				return format.id !== NotificationDocumentTypeId.Html;
			});
		}
		else {
			return this.notificationTemplateFormats;
		}
	}

	private skipNotify(): void {
		if(!this.notifyOpenedManually && (this.useSingleNotificationUI || !this.isPredefineTemplateEnabled)) {
			this.skipSingleRule();
			return;
		}

		// Locally skip the template
		this.setSkipForCurrentTemplate(true);

		// Move onto next template
		this.onNextFooterStep();
	}

	private unskipNotify(): void {
		// Locally unskip the template
		this.setSkipForCurrentTemplate(false);
	}

	private editNotify(): void {
		this.currentNotificationPreviouslySent = false;
		this.notificationsToSend[this.footerStep - 1].isPreviouslySent = false;
	}

	private setSkipForCurrentTemplate(value: boolean): void {
		this.$set(this.notificationsToSend, this.footerStep - 1, {
			...this.notificationsToSend[this.footerStep - 1],
			skipped: value
		});
	}

	@Emit("close-modal")
	private sendingCancel(): boolean {
		return true;
	}

	@Emit("email-loading")
	private emailLoading(emailsLoaded: boolean): boolean {
		return emailsLoaded;
	}

	@Emit("focus-changed")
	private onInputFocusChanged(focus: boolean): boolean {
		if (this.isPoppedOut) {
			return focus;
		}
	}

	@Emit("notify-rule-reload")
	private emitNotifyRuleReload(notifyRuleReload): NotifyRuleReload {
		return notifyRuleReload;
	}

	@Emit("notify-sent")
	private notifySent(notifySent: NotifySent): NotifySent {
		return notifySent;
	}

	@Watch("selectedNotificationTemplate")
	@Emit("template-changed")
	private onTemplateChanged(newTemplate: NotificationTemplate): NotificationTemplate {
			// If no template format is selected, set to default format (Logic for format is in setDefaultTemplateFormat method)
			if (!this.currentNotificationToSend?.notificationTemplateFormat
				&& newTemplate
				&& this.isNotificationTemplatesEnabled
				&& this.notifyOpenedManually) {
				this.setDefaultTemplateFormat();
			}

		return newTemplate;
	}

	private async loadDefaultTemplate(): Promise<void> {
		this.defaultNotificationTemplateId = await organizationApi.GetDefaultNotificationTemplateId(this.groupId);
		let selectedTemplate = null;

		if (this.defaultNotificationTemplateId == null && (
				this.allNotificationTemplates == null
				|| this.allNotificationTemplates.length === 1)
		) {
			// If no default returned and there is only one option in the list, use the first template in the list
			selectedTemplate = this.allNotificationTemplates[0] ?? null;
		}

		// If selectedTemplate is still null, try to find the default template by ID
		selectedTemplate = selectedTemplate ?? this.allNotificationTemplates.firstOrDefault((template: NotificationTemplate) => {
			return template.notificationTemplateId === this.defaultNotificationTemplateId;
		});

		if (selectedTemplate) {
			this.currentNotificationToSend.notificationTemplate = selectedTemplate;
			this.selectedNotificationTemplate = selectedTemplate;

			// Load the manual/custom fields in ad hoc Notify modal
			this.updateAvailableManualFields(selectedTemplate, null);
		}
	}

	private get showOverlay(): boolean {
		return this.currentNotificationSkipped || this.currentNotificationPreviouslySent;
	}

	private get isPreviouslySent(): boolean {
		return this.currentNotificationToSend?.isPreviouslySent;
	}

	// Use NotificationMixins for validation
	private updateNotificationValidationErrors(): void {
		if (this.notificationRulesHasNoTemplate) {
			this.manualValidation();
			return;
		}

		NotificationMixins.methods.updateNotificationValidationErrors(
			this.notificationsToSend,
			this.eventOutcome,
			this.hasCustomFieldPermissions,
			this.notifyNotificationRules,
			this.notificationValidationErrorsByTemplate
		);
		// Force reactivity if needed
		this.notificationValidationErrorsByTemplate = new Map(this.notificationValidationErrorsByTemplate);
	}

	private validateNotification(notification: NotificationState): void {
		NotificationMixins.methods.validateNotification(
			notification,
			this.eventOutcome,
			this.hasCustomFieldPermissions,
			this.orderedCustomFieldList,
			this.notificationValidationErrorsByTemplate,
		);
		this.notificationValidationErrorsByTemplate = new Map(this.notificationValidationErrorsByTemplate);
	}

	private quickSkipNotify(): void {
		if (this.notifyOpenedManually || !this.isQuickEndingEvent) {
			return;
		}

		// Pass in true to skip all templates
		this.handleSend(true);
	}

	private get isOnFinalTemplateStep(): boolean {
		return this.footerStep === this.footerStepTotal;
	}

	private get maxFooterStep(): number {
		return this.notificationsToSend.length;
	}

	private get isOnLandingPageFooterStep(): boolean {
		return this.footerStep === this.landingPageFooterStep;
	}

	private isOptionInRuleAndNotSelected(option: ContactGroup): boolean {
		if (!this.canUseNotificationRules) {
			return false;
		}

		const optionInRule = this.notificationRuleContactGroups.some(cg => cg.contactGroupId === option.contactGroupId);
		const optionSelected = this.currentNotificationToSend.contactGroups.some(cg => cg.contactGroupId === option.contactGroupId);

		return optionInRule && !optionSelected;
	}

	private async loadTemplatesFromApi(): Promise<void> {
		await this.loadNotificationTemplates();
		await this.retrieveSavedTemplates();
		await this.retrieveSentTemplates();
	}

	private async loadTemplatesFromRules(): Promise<void> {
		if (!this.isPredefineTemplateEnabled
			|| !this.notifyNotificationRules
			|| !this.notifyNotificationRules.rules) {
			return;
		}

		this.allNotificationTemplates = this.notifyNotificationRules.rules
			.filter(rule => rule.notificationTemplateId != null && rule.notificationTemplate != null)
			.map(rule => rule.notificationTemplate)
			.filter((template, index, self) => self.findIndex(t => t.notificationTemplateId === template.notificationTemplateId) === index); // Remove duplicates

		await this.retrieveSavedTemplates();
		await this.retrieveSentTemplates();
	}

	@Watch("currentNotificationToSend", { deep: true })
		private onManualNotificationChangeValidate(): void {
		if (this.currentNotificationToSend) {
			this.currentNotificationPreviouslySent = this.currentNotificationToSend.isPreviouslySent ?? false;
			this.currentNotificationSkipped = this.currentNotificationToSend.skipped ?? false;
		}

		if (!this.currentNotificationToSend?.notificationTemplate
			&& this.currentNotificationToSend?.notificationTemplateFormat
			&& this.notifyOpenedManually
		) {
			// If the template has been cleared, also clear the format
			this.currentNotificationToSend.notificationTemplateFormat = null;
			this.selectedTemplateFormat = null;
		}

		if (!this.isPredefineTemplateEnabled || this.useSingleNotificationUI || this.notifyOpenedManually) {
			this.manualValidation();

			const currentNotificationIsBlank = NotificationMixins.methods.notificationsAreEqual(this.currentNotificationToSend, BlankNotification as NotificationState);
			if (!this.savedNotificationsByTemplateId.has(this.currentNotificationToSend?.notificationTemplate?.notificationTemplateId)) {
				// If no notification is saved, enabled the 'Saved' button when any values are provided to the current Notification
				this.isNotificationChangedFromSaved = !currentNotificationIsBlank;
			}
			else {
				// If a saved template exists, verify that the current notification differs from the saved one (and is not blank)
				var savedInformationForTemplate = this.savedNotificationsByTemplateId.get(this.currentNotificationToSend.notificationTemplate?.notificationTemplateId);
				const notificationsMatch = NotificationMixins.methods.notificationsAreEqual(
					savedInformationForTemplate,
					this.currentNotificationToSend,
					this.isContactGroupsEnabled,
					this.isNotificationNoteEnabled
				);
				this.isNotificationChangedFromSaved = !currentNotificationIsBlank && !notificationsMatch;
			}
		}

		if (this.currentNotificationToSend && this.notifyNotificationRules && this.notifyNotificationRules?.rules) {
			// Clone any rules before modifying so we do not mutate the original notifyNotificationRules objects
			const rulesByTemplate = this.triggeredNotificationRules.get(this.currentNotificationToSend?.notificationTemplate?.notificationTemplateId) ?? [];
			const rulesWithNoTemplate = this.triggeredNotificationRules.get(this.nonTemplateRuleId) ?? [];

			// Use deep clones to avoid changing references that exist on notifyNotificationRules.rules
			const clonedRules: NotificationRule[] = [
				...rulesByTemplate.map(r => cloneDeep(r)),
				...rulesWithNoTemplate.map(r => cloneDeep(r))
			];

			if (!this.isPredefineTemplateEnabled) {
				clonedRules.forEach(rule => {
					rule.notificationTemplate = null;
					rule.notificationTemplateId = null;
				});
			}

			if (clonedRules.length > 0) {
				this.setNotificationRulesContributingToNotification(clonedRules);
			}
		}
	}

	@Watch("eventOutcome", { deep: true })
	private onEventOutcomeChangeManualValidate(): void {
		this.manualValidation();
	}

	private manualValidation(): void {
		// Skip manual validation if validating multiple templates
		if (!this.notifyOpenedManually && this.isPredefineTemplateEnabled && !this.notificationRulesHasNoTemplate) {
			return;
		}

		if (!this.currentNotificationToSend) {
			this.manualNotificationIsValid = false;
			return;
		}

		// Start with basic validation - contacts, groups, and note
		let singleNotificationValid =
			this.addressesValid &&
			this.contactGroupsValid &&
			this.noteValid &&
			this.eventOutcome.valid;

		// If no template selected, we're done with basic validation
		if (!this.currentNotificationToSend.notificationTemplate) {
			this.manualNotificationIsValid = singleNotificationValid;
			return;
		}

		// Template is selected - additional validation required
		// Check if template format is required and selected
		if (this.requiresNotificationTemplateType) {
			singleNotificationValid = singleNotificationValid &&
				this.currentNotificationToSend.notificationTemplateFormat != null;
		}

		// Validate custom fields if applicable
		if (this.canDisplayCustomFields) {
			const [requiredCustomFieldsLeftEmpty, customFieldsContainInvalidValues] =
				NotificationMixins.methods.checkCustomFieldValidation(
					this.currentNotificationToSend,
					this.orderedCustomFieldList
				);

			singleNotificationValid = singleNotificationValid &&
				!requiredCustomFieldsLeftEmpty &&
				!customFieldsContainInvalidValues;
		}

		this.manualNotificationIsValid = singleNotificationValid;
	}

	private get sendButtonEnabled(): boolean {
		if (this.notifyOpenedManually || !this.isPredefineTemplateEnabled || this.useSingleNotificationUI) {
			return this.manualNotificationIsValid;
		} else {
			return !this.errorsExistInTemplates && this.isOnLandingPageFooterStep;
		}
	}

	private get saveButtonEnabled(): boolean {
		return this.isNotificationChangedFromSaved && !isEqual(this.currentNotificationToSend, BlankNotification) && !!this.selectedNotificationTemplate;
	}

	private get noteValid(): boolean {
		if (!this.isNotificationNoteEnabled) {
			return true;
		}

		return this.currentNotificationToSend?.note.length <= NotificationNoteMaxLength;
	}

	private get noteInvalidFeedback(): string {
		if (this.isNotificationNoteEnabled && this.currentNotificationToSend.note.length > NotificationNoteMaxLength) {
			return `Note must have no more than ${NotificationNoteMaxLength} characters.`;
		}

		return "";
	}

	private get addressesValid(): boolean {
		// If no notification, invalid
		if  (!this.currentNotificationToSend) {
			return false;
		}

		// If has contacts, valid
		if (this.currentNotificationToSend.contacts && this.currentNotificationToSend.contacts.length > 0) {
			return true;
		}

		// If no contacts (previous check), invalid if the feature is disabled for Contact Groups
		if (!this.isContactGroupsEnabled) {
			return false;
		}

		// if no contacts, valid if there is at least one Contact Group
		const hasContactGroups = this.currentNotificationToSend.contactGroups && this.currentNotificationToSend.contactGroups.length > 0;
		return hasContactGroups;
	}

	private get notificationRulesHasNoTemplate(): boolean {
		if (!this.notifyNotificationRules || !this.notifyNotificationRules.rules) {
			return false;
		}

		if (this.notifyOpenedManually) {
			return false;
		}

		return this.notifyNotificationRules.rules.every(rule => !rule.notificationTemplateId);
	}

	private get addressesInvalidFeedback(): string {
		if (this.currentNotificationToSend.contacts?.length === 0) {
			let feedbackString = "Please specify at least one recipient";

			if (this.isContactGroupsEnabled) {
				feedbackString = feedbackString.concat(" (or contact group)");
			}

			return feedbackString;
		}

		return "";
	}

	private get contactGroupsValid(): boolean {
		// If no notification, invalid
		if (!this.currentNotificationToSend) {
			return false;
		}

		// If one or more contacts, valid
		if (this.currentNotificationToSend.contacts && this.currentNotificationToSend.contacts.length > 0) {
			return true;
		}

		// If no contacts and feature is disabled, invalid
		if (!this.isContactGroupsEnabled) {
			return false;
		}

		// If no contacts, valid only if contact groups exist
		const hasContactGroups = this.currentNotificationToSend.contactGroups && this.currentNotificationToSend.contactGroups.length > 0;
		return hasContactGroups;
	}

	private get contactGroupsInvalidFeedback(): string {
		const hasGroups = this.currentNotificationToSend.contactGroups && this.currentNotificationToSend.contactGroups.length > 0;
		const hasContacts = this.currentNotificationToSend.contacts && this.currentNotificationToSend.contacts.length > 0;
		if (!(hasGroups || hasContacts)) {
			return "Please specify at least one contact group (or contact)";
		}
		return "";
	}

	private get noteValidFeedback(): string {
		const charactersUsed = this.currentNotificationToSend.note !== null ? this.currentNotificationToSend.note.length : 0;
		return `Characters used: ${charactersUsed}/${NotificationNoteMaxLength}`;
	}

	private get previouslySendDetails(): SentOverlayDetails {
		const sentTemplate = this.sentTemplates.find(st => st.notificationTemplateId === this.selectedNotificationTemplate?.notificationTemplateId);
		const parsedData = NotificationMixins.conversionMethods.eventNotificationToNotification(sentTemplate, this.allNotificationTemplates);
		const contacts = parsedData?.contacts ? parsedData.contacts.map(c => c.fullName || c.email).join(", ") : "";
		const contactGroupTitles = parsedData?.contactGroups ? parsedData.contactGroups.map(cg => cg.title).join(", ") : "";
		return {
			sentBy: sentTemplate?.userName,
			sentAt: sentTemplate ? new Date(sentTemplate.sentAt).toLocaleString(): null,
			notificationTitle: this.currentNotificationToSend.notificationTemplate?.title,
			contactsNames: contacts,
			contactGroupsTitles: contactGroupTitles,
			hasAdditionalContacts: this.currentNotificationToSend.hasContactsDifferentToRule,
		}
	}

	private get allTemplatesSkipped(): boolean {
		return this.notificationsToSend.every(n => n.skipped);
	}

	private onActionPlanResult(value: boolean): void {
		this.showActionPlanAlert = value;
	}

	private isOptionHighlighted(option: ContactGroup): boolean {
		if (!this.highlightedContactGroups || this.highlightedContactGroups.length === 0) {
			return false;
		}

		return this.highlightedContactGroups.some(hc => hc.contactGroupId === option.contactGroupId);
	}

	private setRecipientsTouched(): void {
		if (!this.recipientInputsTouched) {
			this.recipientInputsTouched = true;
		}
	}

	private get requiresNotificationTemplateType(): boolean {
		// Template format required when filling out predefined templates
		if (this.isPredefineTemplateEnabled && !this.notifyOpenedManually && !this.notificationRulesHasNoTemplate) {
			return true;
		}

		// No template type necessary when no notification (e.g. landing page)
		if (!this.currentNotificationToSend) {
			return false;
		}

		// Template Format necessary if Notification Template is selected
		if (this.currentNotificationToSend.notificationTemplate != null) {
			return true;
		}

		// Otherwise, template type always required
		return false;
	}

	private get hasDataBeenLoadedFromSaved(): boolean {
		if (!this.currentNotificationToSend || !this.currentNotificationToSend.notificationTemplate) {
			return false;
		}

		return this.savedNotificationsByTemplateId.has(this.currentNotificationToSend.notificationTemplate.notificationTemplateId);
	}

	private onSummaryGenerated(summary: string): void {
		this.currentNotificationToSend.note = summary;
	}

	private get isEventOutcomeNoteEnabled(): boolean {
		return this.isNotificationTemplatesEnabled && this.hasNotificationTemplatePermissions
			&& (this.selectedNotificationTemplate != null || !this.notifyOpenedManually && this.useSingleNotificationUI)
	}

	private get sendButtonTooltip(): string {
		if  (this.errorsExistInTemplates && this.isOnLandingPageFooterStep) {
            return 'All templates must be completed, free of errors, or skipped before notifications can be sent.';
        }

		if  (!this.useSingleNotificationUI && !this.isOnLandingPageFooterStep && !this.notifyOpenedManually) {
            return 'Templates can be sent once all are completed or skipped.';
        }

		return '';
    }

	private get showQuickSkipButton(): boolean {
		return !this.notifyOpenedManually
			&& !this.isOnLandingPageFooterStep
			&& this.isQuickEndingEvent
			&& this.isPredefineTemplateEnabled
			&& !this.useSingleNotificationUI
			&& this.isComponentReady;
	}

	private get showSkipAllTooltip(): boolean {
		return this.isOnLandingPageFooterStep
			&& this.allTemplatesSkipped
			&& this.isComponentReady
			&& !this.notifyOpenedManually
			&& this.showTooltip;
	}
}
